/**
 * RateHistoryChart — Story 1.4 AC-1
 *
 * Interaktiver Line-Chart des $/GB-Verlaufs mit hinterlegten Floor- (0,5x)
 * und Cap-Baendern (3,0x). Referenz: claude-design-import/earn-rate.html
 * (SVG 760x320, padX 44, padY 24, gestrichelte Floor-/Cap-Linien,
 * Flaechen-Gradient unter der Kurve).
 *
 * Interaktion: Pointer-Hover zeigt Crosshair + Tooltip (Datum, Rate, GB).
 * Tage ohne belastbaren Wert (rateUsd = null) erscheinen als Luecke.
 * Nur Design-Tokens (--primary, --rate-floor, --rate-cap, --border, ...).
 */
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { formatNumber, formatRate } from '../../components/ui'
import { clamp } from '../../components/ui/format'
import {
  accountAverageRate,
  CAP_BAND_USD,
  FLOOR_BAND_USD,
  NOVA_CAP_USD,
  NOVA_FLOOR_USD,
  rateChartDomain,
  rateChartGridValues,
  type RateHistoryPoint,
} from './earnRateHistory'

const W = 760
const H = 320
const PAD_X = 44
const PAD_Y = 24

type Props = {
  points: RateHistoryPoint[]
  'data-testid'?: string
}

export function RateHistoryChart({ points, 'data-testid': testId = 'rate-history-chart' }: Props) {
  const { t } = useTranslation('earnrate')
  const svgRef = useRef<SVGSVGElement | null>(null)
  const [hoverIdx, setHoverIdx] = useState<number | null>(null)

  const rates = useMemo(
    () => points.map((p) => p.rateUsd).filter((r): r is number => r != null),
    [points],
  )

  // F2: robuste Skala — ein einzelner Mini-Volumen-Tag (0,02 GB -> 150 $/GB)
  // darf weder die Domain noch die Gridline-Anzahl sprengen. Werte oberhalb
  // der Domain werden geklemmt und als Ausreisser markiert.
  const { min: dMin, max: dMax } = useMemo(() => rateChartDomain(rates), [rates])
  const accountAvg = useMemo(() => accountAverageRate(points), [points])

  const y = (v: number) =>
    H - PAD_Y - ((clamp(v, dMin, dMax) - dMin) / (dMax - dMin)) * (H - 2 * PAD_Y)
  const x = (i: number) =>
    points.length <= 1 ? W / 2 : PAD_X + (i / (points.length - 1)) * (W - 2 * PAD_X)

  // F2: feste Gridline-Anzahl (~6, hart auf 12 begrenzt).
  const gridValues = useMemo(() => rateChartGridValues(dMin, dMax), [dMin, dMax])
  const gridDecimals = gridValues.length > 1 && gridValues[1] - gridValues[0] < 0.01 ? 3 : 2

  // Kurven-Segmente: an null-Werten (Luecken) auftrennen.
  const segments = useMemo(() => {
    const result: Array<Array<{ i: number; v: number }>> = []
    let current: Array<{ i: number; v: number }> = []
    points.forEach((p, i) => {
      if (p.rateUsd == null) {
        if (current.length > 0) result.push(current)
        current = []
      } else {
        current.push({ i, v: p.rateUsd })
      }
    })
    if (current.length > 0) result.push(current)
    return result
  }, [points])

  // Auf die Skala geklemmte Ausreisser (Marker + Tooltip-Hinweis).
  const clippedIdx = useMemo(
    () =>
      points
        .map((p, i) => (p.rateUsd != null && p.rateUsd > dMax ? i : -1))
        .filter((i) => i >= 0),
    [points, dMax],
  )

  let lastPoint: { i: number; v: number } | null = null
  for (let i = points.length - 1; i >= 0; i--) {
    const rate = points[i].rateUsd
    if (rate != null) {
      lastPoint = { i, v: rate }
      break
    }
  }

  const handleMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const svg = svgRef.current
    if (!svg || points.length === 0) return
    const rect = svg.getBoundingClientRect()
    const fx = ((e.clientX - rect.left) / rect.width) * W
    const idx = Math.round(((fx - PAD_X) / (W - 2 * PAD_X)) * (points.length - 1))
    setHoverIdx(clamp(idx, 0, points.length - 1))
  }

  const hover = hoverIdx != null ? points[hoverIdx] : null
  const hoverLeftPct = hoverIdx != null ? (x(hoverIdx) / W) * 100 : 0

  const ariaLabel = t('chart.ariaLabel', {
    days: points.length,
    last: lastPoint ? formatRate(lastPoint.v, '$/GB', 3) : t('chart.noValue'),
  })

  return (
    <div className="er-chart-scroll" data-testid={testId}>
      <div className="er-chart-hover-area">
        <svg
          ref={svgRef}
          className="er-chart"
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          role="img"
          aria-label={ariaLabel}
          data-testid={`${testId}-svg`}
          onPointerMove={handleMove}
          onPointerLeave={() => setHoverIdx(null)}
        >
          {/* Cap-Band (oberhalb 0,24 $) + Floor-Band (unterhalb 0,08 $) */}
          <rect
            data-testid={`${testId}-cap-band`}
            x={PAD_X}
            y={y(dMax)}
            width={W - 2 * PAD_X}
            height={Math.max(0, y(CAP_BAND_USD) - y(dMax))}
            fill="var(--rate-cap)"
            opacity="0.10"
          />
          <rect
            data-testid={`${testId}-floor-band`}
            x={PAD_X}
            y={y(FLOOR_BAND_USD)}
            width={W - 2 * PAD_X}
            height={Math.max(0, y(dMin) - y(FLOOR_BAND_USD))}
            fill="var(--rate-floor)"
            opacity="0.10"
          />

          {/* Floor-/Cap-Linien (0,050 / 0,300 $) */}
          <line
            data-testid={`${testId}-floor-line`}
            x1={PAD_X}
            x2={W - PAD_X}
            y1={y(NOVA_FLOOR_USD)}
            y2={y(NOVA_FLOOR_USD)}
            stroke="var(--rate-floor)"
            strokeWidth="1.5"
            strokeDasharray="5 4"
          />
          <text
            x={W - PAD_X - 4}
            y={y(NOVA_FLOOR_USD) - 6}
            textAnchor="end"
            fontSize="10"
            fill="var(--rate-floor)"
            fontFamily="var(--font-mono)"
          >
            {t('chart.floorLine', { value: formatRate(NOVA_FLOOR_USD, '$', 3) })}
          </text>
          <line
            data-testid={`${testId}-cap-line`}
            x1={PAD_X}
            x2={W - PAD_X}
            y1={y(NOVA_CAP_USD)}
            y2={y(NOVA_CAP_USD)}
            stroke="var(--rate-cap)"
            strokeWidth="1.5"
            strokeDasharray="5 4"
          />
          <text
            x={W - PAD_X - 4}
            y={y(NOVA_CAP_USD) + 14}
            textAnchor="end"
            fontSize="10"
            fill="var(--rate-cap)"
            fontFamily="var(--font-mono)"
          >
            {t('chart.capLine', { value: formatRate(NOVA_CAP_USD, '$', 3) })}
          </text>

          {/* Account-Ø-Referenzlinie (ACCEPTANCE: "account-Ø line distinct") */}
          {accountAvg != null && (
            <line
              data-testid={`${testId}-account-avg-line`}
              x1={PAD_X}
              x2={W - PAD_X}
              y1={y(accountAvg)}
              y2={y(accountAvg)}
              stroke="var(--accent)"
              strokeWidth="1.5"
              strokeDasharray="2 3"
              opacity="0.85"
            />
          )}

          {/* Grid + Y-Achsen-Labels */}
          {gridValues.map((v) => (
            <g key={v}>
              <line
                x1={PAD_X}
                x2={W - PAD_X}
                y1={y(v)}
                y2={y(v)}
                stroke="var(--border)"
                strokeDasharray="2 4"
              />
              <text
                x={PAD_X - 6}
                y={y(v) + 3}
                textAnchor="end"
                fontSize="10"
                fill="var(--text-subtle)"
                fontFamily="var(--font-mono)"
              >
                {formatNumber(v, gridDecimals)}
              </text>
            </g>
          ))}

          {/* Flaeche + Kurve (je Segment, Luecken bleiben leer) */}
          <defs>
            <linearGradient id="er-area-gradient" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.22" />
              <stop offset="100%" stopColor="var(--primary)" stopOpacity="0" />
            </linearGradient>
          </defs>
          {segments.map((segment) => {
            const pts = segment.map((p) => `${x(p.i).toFixed(1)},${y(p.v).toFixed(1)}`).join(' ')
            const first = segment[0]
            const last = segment[segment.length - 1]
            const area = `${x(first.i).toFixed(1)},${y(dMin).toFixed(1)} ${pts} ${x(last.i).toFixed(1)},${y(dMin).toFixed(1)}`
            return (
              <g key={first.i}>
                {segment.length > 1 && <polygon points={area} fill="url(#er-area-gradient)" />}
                <polyline
                  data-testid={`${testId}-line`}
                  points={pts}
                  fill="none"
                  stroke="var(--primary)"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
              </g>
            )
          })}

          {/* Letzter Wert */}
          {lastPoint && (
            <circle
              cx={x(lastPoint.i)}
              cy={y(lastPoint.v)}
              r="4"
              fill="var(--primary)"
              stroke="var(--surface)"
              strokeWidth="2"
            />
          )}

          {/* F2: geklemmte Ausreisser als Dreieck am oberen Rand */}
          {clippedIdx.map((i) => (
            <polygon
              key={`clip-${i}`}
              data-testid={`${testId}-clipped-marker`}
              className="er-clip-marker"
              points={`${x(i).toFixed(1)},${(PAD_Y - 4).toFixed(1)} ${(x(i) - 5).toFixed(1)},${(PAD_Y + 5).toFixed(1)} ${(x(i) + 5).toFixed(1)},${(PAD_Y + 5).toFixed(1)}`}
            >
              <title>{t('chart.clippedMarker')}</title>
            </polygon>
          ))}

          {/* Hover-Crosshair */}
          {hoverIdx != null && (
            <g data-testid={`${testId}-crosshair`}>
              <line
                x1={x(hoverIdx)}
                x2={x(hoverIdx)}
                y1={PAD_Y}
                y2={H - PAD_Y}
                stroke="var(--text-subtle)"
                strokeWidth="1"
                strokeDasharray="3 3"
              />
              {points[hoverIdx].rateUsd != null && (
                <circle
                  cx={x(hoverIdx)}
                  cy={y(points[hoverIdx].rateUsd as number)}
                  r="5"
                  fill="var(--primary)"
                  stroke="var(--surface)"
                  strokeWidth="2"
                />
              )}
            </g>
          )}
        </svg>

        {hover && (
          <div
            className="er-chart-tooltip card"
            data-testid={`${testId}-tooltip`}
            style={{
              left: `${hoverLeftPct}%`,
              transform: hoverLeftPct > 70 ? 'translateX(-100%)' : 'translateX(8px)',
            }}
            aria-hidden="true"
          >
            <div className="er-chart-tooltip-date">{hover.date}</div>
            <div className="er-chart-tooltip-rate tnum">
              {hover.rateUsd != null
                ? formatRate(hover.rateUsd, '$/GB', 3)
                : t('chart.tooltipNoRate')}
            </div>
            <div className="er-chart-tooltip-gb muted tnum">
              {t('chart.tooltipGb', { value: formatNumber(hover.gb, 2) })}
            </div>
            {hover.rateUsd != null && hover.rateUsd > dMax && (
              <div className="er-chart-tooltip-gb muted" data-testid={`${testId}-tooltip-clipped`}>
                {t('chart.tooltipClipped')}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="er-legend">
        <span className="er-legend-item">
          <span className="er-legend-dot" style={{ background: 'var(--primary)' }} />
          {t('chart.legendRate')}
        </span>
        <span className="er-legend-item">
          <span className="er-legend-dot" style={{ background: 'var(--rate-floor)', opacity: 0.4 }} />
          {t('chart.legendFloor', { value: formatRate(FLOOR_BAND_USD, '$', 2) })}
        </span>
        <span className="er-legend-item">
          <span className="er-legend-dot" style={{ background: 'var(--rate-cap)', opacity: 0.4 }} />
          {t('chart.legendCap', { value: formatRate(CAP_BAND_USD, '$', 2) })}
        </span>
        {accountAvg != null && (
          <span className="er-legend-item" data-testid={`${testId}-account-avg-legend`}>
            <span className="er-legend-dot" style={{ background: 'var(--accent)' }} />
            {t('chart.legendAccountAvg', { value: formatRate(accountAvg, '$/GB', 3) })}
          </span>
        )}
      </div>
    </div>
  )
}
